/**
 * 指挥页外发指令位置点：WS tracker_command_dispatch + GET /command-pins hydrate
 * 「从地图移除」调用后端永久软隐藏（map_hidden_at），刷新后不再出现。
 */
import { useCallback, useEffect, useRef } from 'react';
import { message } from '../utils/antdMessage';
import { apiFetch } from '../utils/api';
import {
  upsertCommandPinOnMap,
  removeCommandPinFromMap,
  reprojectCommandPins,
  clearAllCommandPins,
  fetchActiveCommandPins,
  normalizeCommandPinPayload,
  removeRescueMarkersCoveredByCommandPins
} from '../utils/dashboard/commandPinHelpers';
import { hasCommandFeature, COMMAND_FEATURE_PERMS } from '../utils/permissionControl';

export default function useDashboardCommandPins({ getMap, rescueTargetMarkersRef }) {
  const pinsMapRef = useRef(new Map());
  const rescueMarkersRefInner = useRef(rescueTargetMarkersRef);
  rescueMarkersRefInner.current = rescueTargetMarkersRef;

  useEffect(() => () => {
    clearAllCommandPins(pinsMapRef.current);
  }, []);

  const suppressOverlappingRescueMarkers = useCallback(() => {
    removeRescueMarkersCoveredByCommandPins(
      rescueMarkersRefInner.current,
      pinsMapRef.current
    );
  }, []);

  const hideCommandPinPermanent = useCallback(async (id) => {
    const batchId = String(id || '').trim();
    if (!batchId) return false;
    try {
      let response = await apiFetch(`/api/tracker/command-pins/${encodeURIComponent(batchId)}`, {
        method: 'DELETE'
      });
      if (response.status === 404 || response.status === 405) {
        response = await apiFetch('/api/tracker/command-pins/hide', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ batch_id: batchId, id: batchId })
        });
      }
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success === false) {
        message.error(data.error || '永久移除指令点失败');
        return false;
      }
      message.success('已永久从地图移除指令点');
      return true;
    } catch (error) {
      console.warn('永久移除指令点失败:', error);
      message.error('永久移除指令点失败');
      return false;
    }
  }, []);

  const placeCommandPin = useCallback((raw) => {
    if (!hasCommandFeature(COMMAND_FEATURE_PERMS.MEMBER_COMMAND)) return;
    const pin = normalizeCommandPinPayload(raw);
    if (!pin) return;
    const map = getMap?.();
    if (!map) return;
    upsertCommandPinOnMap(map, pin, pinsMapRef.current, {
      onHidden: async (id) => {
        removeCommandPinFromMap(pinsMapRef.current, id);
        const ok = await hideCommandPinPermanent(id);
        if (!ok) {
          // 失败则重新 hydrate，避免仅本地消失
          try {
            const pins = await fetchActiveCommandPins();
            const m = getMap?.();
            if (m) pins.forEach((p) => placeCommandPin(p));
          } catch (_) { /* ignore */ }
        }
      }
    });
    // 发送位置双写时同坐标优先指令点，去掉重叠的救援针
    suppressOverlappingRescueMarkers();
  }, [getMap, hideCommandPinPermanent, suppressOverlappingRescueMarkers]);

  const handleTrackerCommandDispatch = useCallback((payload) => {
    if (!payload) return;
    if (!hasCommandFeature(COMMAND_FEATURE_PERMS.MEMBER_COMMAND)) return;
    placeCommandPin(payload);
  }, [placeCommandPin]);

  const handleTrackerCommandPinRemoved = useCallback((payload) => {
    const ids = [];
    if (payload?.batch_id) ids.push(String(payload.batch_id));
    if (payload?.id) ids.push(String(payload.id));
    if (Array.isArray(payload?.batch_ids)) {
      payload.batch_ids.forEach((id) => ids.push(String(id)));
    }
    [...new Set(ids.filter(Boolean))].forEach((id) => {
      removeCommandPinFromMap(pinsMapRef.current, id);
    });
  }, []);

  /** 发送下达成功回包 / 会话外发条目时即时上图 */
  const placeOutboundCommandPin = useCallback((entry) => {
    if (!entry) return;
    if (!(entry.pin_on_map || entry.command_kind === 'location')) return;
    const lat = entry.latitude != null ? Number(entry.latitude) : NaN;
    const lng = entry.longitude != null ? Number(entry.longitude) : NaN;
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    placeCommandPin({
      batch_id: entry.batch_id || entry.id,
      id: entry.batch_id || entry.id,
      text: entry.text,
      important: entry.important,
      latitude: lat,
      longitude: lng,
      command_kind: entry.command_kind || 'location',
      created_by: entry.created_by,
      created_by_name: entry.created_by_name,
      created_by_label: entry.created_by_label,
      created_at: entry.created_at
    });
  }, [placeCommandPin]);

  const loadCommandPins = useCallback(async () => {
    if (!hasCommandFeature(COMMAND_FEATURE_PERMS.MEMBER_COMMAND)) return;
    try {
      const pins = await fetchActiveCommandPins();
      const map = getMap?.();
      if (!map) return;
      pins.forEach((pin) => placeCommandPin(pin));
      suppressOverlappingRescueMarkers();
    } catch (error) {
      console.warn('加载指令位置点失败:', error.message || error);
    }
  }, [getMap, placeCommandPin, suppressOverlappingRescueMarkers]);

  const reprojectCommandPinsOnMap = useCallback((basemapId) => {
    const map = getMap?.();
    if (!map) return;
    reprojectCommandPins(map, pinsMapRef.current, basemapId);
  }, [getMap]);

  return {
    handleTrackerCommandDispatch,
    handleTrackerCommandPinRemoved,
    placeOutboundCommandPin,
    loadCommandPins,
    reprojectCommandPinsOnMap,
    commandPinsMapRef: pinsMapRef
  };
}
